import { FC, ReactElement, useMemo } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";
import { QesColors } from "@app/shared/qes-standards/colors";
import { JobCompletionCountResult } from "@app/operations/jobStatisticsResult";
import { toJobCompletionPoints } from "@app/operations/job-completion-rate/jobCompletionChartData";
import { JobCompletionGranularity } from "@app/operations/job-completion-rate/types";

export interface JobCompletionRateChartProps {
  data: JobCompletionCountResult[];
  /** The granularity the data was queried at, which sets the width of a filled gap bucket. */
  granularity: JobCompletionGranularity;
}

export function formatBucketStart(value: string): string {
  return new Date(value).toLocaleString("en-US", { day: "2-digit", hour: "2-digit", minute: "2-digit", month: "2-digit", year: "numeric" });
}

function renderTooltipContent({ active, payload, label }: TooltipContentProps<number, string>): ReactElement | null {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="job-completion-rate-chart__tooltip bg-white border rounded p-2" data-testid="job-completion-rate-chart__tooltip">
      <div>{formatBucketStart(String(label))}</div>
      {payload.map(entry => (
        <div key={entry.name}>{entry.name}: {entry.value}</div>
      ))}
    </div>
  );
}

export const JobCompletionRateChart: FC<JobCompletionRateChartProps> = ({ data, granularity }) => {
  const { gapsFilled, points } = useMemo(() => toJobCompletionPoints(data, granularity), [data, granularity]);

  if (points.length === 0) {
    return <p data-testid="job-completion-rate-chart__empty">No completed jobs found for the selected filters.</p>;
  }

  return (
    <div className="job-completion-rate-chart">
      {!gapsFilled && (
        <p className="text-muted" data-testid="job-completion-rate-chart__unfilled-gaps">
          This range is too wide to plot every time bucket, so intervals with no completed jobs are omitted. Narrow the date range or switch to Hour granularity to see them.
        </p>
      )}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={points} margin={{ bottom: 20, left: 20, right: 30, top: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucketStart" tickFormatter={formatBucketStart} />
          <YAxis allowDecimals={false} label={{ angle: -90, position: "left", style: { textAnchor: "middle" }, value: "Completed Jobs" }} />
          <Legend />
          <Line dataKey="successCount" dot={false} isAnimationActive={false} name="Successful" stroke={QesColors.QesPrimaryGreen} type="monotone" />
          <Line dataKey="failureCount" dot={false} isAnimationActive={false} name="Failed" stroke={QesColors.QesPrimaryRed} type="monotone" />
          <Line connectNulls={true} dataKey="successTrend" dot={false} isAnimationActive={false} name="Successful Trend" stroke={QesColors.QesPrimaryGreen} strokeDasharray="5 4" strokeOpacity={0.55} type="linear" />
          <Line connectNulls={true} dataKey="failureTrend" dot={false} isAnimationActive={false} name="Failed Trend" stroke={QesColors.QesPrimaryRed} strokeDasharray="5 4" strokeOpacity={0.55} type="linear" />
          <Tooltip content={renderTooltipContent} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default JobCompletionRateChart;
